import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Download, FileText, Lock, ShieldCheck, BookOpen } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import CinemaPageHero from "@/components/site/CinemaPageHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (amount) =>
  `${Number(amount).toFixed(2).replace(".", ",")} €`;

function ProductCover({ product }) {
  if (product.cover) {
    return (
      <img
        src={product.cover}
        alt={`Cover: ${product.title}`}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
      />
    );
  }
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-cream-200 to-peach-50 flex flex-col justify-between p-8">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-brand-red mb-4">
          {product.format || "PDF"}
        </div>
        <h3 className="font-heading font-bold text-xl md:text-2xl text-sand-800 leading-tight tracking-tight">
          {product.title}
        </h3>
        <p className="mt-3 text-sand-600 text-[15px] leading-snug">
          {product.subtitle}
        </p>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-sand-600 font-semibold">
        Studio DH · DRM-frei
      </div>
    </div>
  );
}

export default function Shop() {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Pflegeshop – eBooks & Kursskripte | Dickies-Campus";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Die Pflege-Bibliothek von Dickies-Campus: gedruckte Lehrbücher & Kursskripte bei Amazon – von Behandlungspflege LG1 & LG2 bis PDL. Plus Delegations-Atlas als PDF.";
    axios
      .get(`${API}/ebooks`)
      .then((r) => {
        setProducts(r.data.products);
        setCategories(r.data.categories || []);
      })
      .catch(() => setError("Der Shop konnte nicht geladen werden – bitte später erneut versuchen."));
  }, []);

  const visibleProducts = products?.filter(
    (p) => activeCategory === "Alle" || p.category === activeCategory
  );

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />
      <main data-testid="shop-page" className="pb-24 lg:pb-32">
        <CinemaPageHero
          image="/images/cinema/shop-wide.jpg"
          imagePosition="center 45%"
          kicker="Pflegeshop · Studio DH"
          title="Wissen, das sich"
          accent="auszahlt."
          sub="Die Pflege-Bibliothek der Dickies-Helden-Akademie: Unsere Lehrbücher und der Pflegekalender 2027 erscheinen als gedruckte Ausgaben bei Amazon – mit kostenloser Leseprobe. Der Delegations-Atlas ist sofort als PDF erhältlich."
          testId="shop-cinema-hero"
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20">
          {error && (
            <p data-testid="shop-error" className="text-terra font-medium">{error}</p>
          )}
          {!products && !error && (
            <p className="text-sand-500 text-sm">Lade Produkte …</p>
          )}

          {/* Category filter */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2.5 mb-12" data-testid="shop-category-filter">
              {["Alle", ...categories].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  data-testid={`shop-filter-${cat.toLowerCase().replace(/[^a-zäöü0-9]+/g, "-")}`}
                  className={`rounded-full px-5 h-10 text-[12.5px] font-semibold tracking-wide transition-all duration-300 border ${
                    activeCategory === cat
                      ? "bg-sand-800 text-cream-50 border-sand-800"
                      : "bg-white text-sand-700 border-cream-300 hover:border-terra hover:text-terra"
                  }`}
                >
                  {cat}
                  {cat !== "Alle" && (
                    <span className="ml-1.5 opacity-80">
                      {products?.filter((p) => p.category === cat).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Product list */}
          <div className="space-y-10 lg:space-y-14">
            {visibleProducts?.map((p, idx) => (
              <Reveal key={p.id} delay={(idx % 3) * 100}>
                <article
                  data-testid={`shop-product-card-${p.id}`}
                  className="group grid md:grid-cols-12 gap-8 lg:gap-12 rounded-3xl bg-white border border-cream-300 shadow-soft hover:shadow-hover transition-all duration-500 p-6 md:p-9 lg:p-10"
                >
                  {/* Cover */}
                  <div className="md:col-span-4">
                    <Link to={`/shop/${p.id}`} className="block relative aspect-[3/4] rounded-2xl overflow-hidden border border-cream-300 bg-cream-100">
                      <ProductCover product={p} />
                      {p.badge && (
                        <span className="absolute top-4 left-4 inline-flex items-center rounded-full bg-terra text-white px-4 py-1.5 text-[11px] font-bold tracking-[0.15em] uppercase shadow-soft">
                          {p.badge}
                        </span>
                      )}
                      {!p.available && (
                        <span className="absolute bottom-4 left-4 inline-flex items-center rounded-full bg-sand-800/90 text-cream-50 px-4 py-1.5 text-[10px] font-bold tracking-[0.15em] uppercase">
                          Erscheint in Kürze
                        </span>
                      )}
                    </Link>
                  </div>

                  {/* Details */}
                  <div className="md:col-span-8 flex flex-col">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-sand-500">
                      {p.category || "Digital"} · {p.channel === "amazon" ? "Gedrucktes Buch" : p.bundle_items ? "Paket" : "eBook"}
                    </div>
                    <Link to={`/shop/${p.id}`} className="mt-2 block">
                      <h2 className="font-heading font-bold text-[1.6rem] md:text-[2rem] text-sand-800 leading-tight tracking-tight group-hover:text-terra transition-colors">
                        {p.title}
                      </h2>
                    </Link>
                    <p className="mt-2 text-base text-sand-600 leading-snug">
                      {p.subtitle}
                    </p>
                    <p className="mt-4 text-sand-700 text-[15px] leading-[1.7] max-w-2xl line-clamp-3">
                      {p.description}
                    </p>

                    <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 max-w-2xl">
                      {p.highlights?.slice(0, 4).map((h) => (
                        <li key={h} className="flex items-start gap-2.5 text-[13.5px] text-sand-800">
                          <span className="mt-0.5 inline-flex h-4.5 w-4.5 items-center justify-center rounded-full bg-terra/15 text-terra shrink-0 p-0.5">
                            <Check className="h-3 w-3" strokeWidth={2.8} />
                          </span>
                          <span className="font-medium">{h}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Price + CTAs */}
                    <div className="mt-7 pt-6 border-t border-cream-300 flex flex-wrap items-center gap-x-8 gap-y-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500">
                          {p.channel === "amazon" ? "Gedruckte Ausgabe · Amazon" : "Einmaliger Kauf · inkl. USt."}
                        </div>
                        <div className="font-heading font-bold text-2xl text-sand-800 tracking-tight" data-testid={`shop-product-price-${p.id}`}>
                          {p.channel === "amazon"
                            ? (p.print_price ? formatPrice(p.print_price) : "Bald erhältlich")
                            : formatPrice(p.amount)}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        {p.channel === "amazon" && p.amazon_url ? (
                          <a
                            href={p.amazon_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`shop-product-cta-${p.id}`}
                            className="inline-flex items-center gap-2 rounded-full bg-sand-800 hover:bg-sand-900 text-cream-50 px-7 h-12 text-[13px] font-semibold transition-colors"
                          >
                            Bei Amazon kaufen
                            <ArrowRight className="h-4 w-4" strokeWidth={2} />
                          </a>
                        ) : (
                          <Link
                            to={`/shop/${p.id}`}
                            data-testid={`shop-product-cta-${p.id}`}
                            className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-7 h-12 text-[13px] font-semibold transition-colors"
                          >
                            {p.channel === "amazon" ? "Zum Buch" : p.available ? "Zum Produkt" : "Details ansehen"}
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                          </Link>
                        )}
                        {p.channel === "amazon" && !p.amazon_url && (
                          <span
                            data-testid={`shop-product-amazon-soon-${p.id}`}
                            className="inline-flex items-center rounded-full bg-cream-200 text-sand-700 px-4 py-1.5 text-[11px] font-bold tracking-[0.12em] uppercase"
                          >
                            Bald bei Amazon
                          </span>
                        )}
                        {p.has_preview && (
                          <a
                            href={`${API}/ebooks/${p.id}/preview`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`shop-product-preview-${p.id}`}
                            className="inline-flex items-center gap-2 text-[13px] font-semibold text-sand-700 hover:text-terra transition-colors"
                          >
                            <FileText className="h-4 w-4" strokeWidth={1.8} />
                            Leseprobe (kostenlos)
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {/* Trust row */}
          <Reveal delay={300}>
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
              {[
                { icon: BookOpen, label: "Gedruckte Bücher über Amazon" },
                { icon: FileText, label: "Kostenlose Leseprobe zu jedem Titel" },
                { icon: Lock, label: "Atlas-PDF: sichere Zahlung über Stripe" },
                { icon: ShieldCheck, label: "SSL-verschlüsselt" },
              ].map((t) => (
                <div key={t.label} className="flex flex-col items-center gap-2.5">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white border border-cream-300 text-brand-red">
                    <t.icon className="h-4.5 w-4.5" strokeWidth={1.6} />
                  </span>
                  <span className="text-[12px] text-sand-600 font-medium leading-snug">{t.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
